import { useMemo } from 'react';
import {
  getCellWidths,
  getColumnByType,
  getRowCells,
  getRowHeight,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

export default function useRankingTable({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds,
  tableName,
  order = 0,
  columnLables,
  hasOrderNumber = false
}) {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const currentDataKey = type === 'AIF001' ? 'AIFOO1' : searchParams.get('fund_id');
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, tableName);
  };
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange = value => {
    const changes = value[0];
    const fund = isMultiFunds
      ? JSON.parse(JSON.stringify(funds[changes.newCell.fundId].fund_data[tableName]))
      : JSON.parse(JSON.stringify(data));

    fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
      cell: changes.newCell
    });
    onChangeFund(tableName, fund, changes.newCell.fundId);
  };

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const columns = useMemo(() => {
    const _fundSize = Object.keys(funds).length;
    const _columns = isMultiFunds
      ? _fundSize <= 3 && !isComparisonReport
        ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / (_fundSize + 1)
          }))
        : isComparisonReport
          ? [
              {
                columnId: 1,
                width: 200
              },
              ...Object.values(funds)
                .map(item => [item, item.previous_fund])
                .flat()
                .map((_, index) => ({
                  columnId: index + 2,
                  width: 300
                }))
            ]
          : [
              {
                columnId: 1,
                width: 200
              },
              ...Object.keys(funds).map((_, index) => ({
                columnId: index + 2,
                width: 300
              }))
            ]
      : isComparisonReport
        ? Array.from(Array(3).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 3
          }))
        : Array.from(Array(2).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 2
          }));
    return [
      {
        columnId: 0,
        resizable: true,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, contentWidth, funds, isComparisonReport]);

  const tables = useMemo(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      isComparisonReport,
      fundSize: Object.keys(funds).length,
      contentWidth
    });
    return data.map((item, index) => {
      const elements = isMultiFunds
        ? isComparisonReport
          ? Object.values(funds)
              .map(fund => [fund, fund.previous_fund])
              .flat()
              .map((fund, i) => ({
                type: 'header',
                text: fund.fund_name || '',
                colspan: i % 2 === 0 ? 2 : undefined
              }))
          : Object.values(funds).map(fund => ({
              type: 'header',
              text: fund.fund_name || ''
            }))
        : isComparisonReport
          ? [
              {
                type: 'header',
                text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
              },
              {
                type: 'header',
                text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
              }
            ]
          : [
              {
                type: 'header',
                text: ''
              }
            ];
      return [
        {
          rowId: 0,
          height: 40,
          cells: [
            {
              type: 'header',
              text: hasOrderNumber ? `${order}` : ''
            },
            {
              type: 'header',
              text: ''
            },
            ...elements
          ]
        },
        ...(isComparisonReport && isMultiFunds
          ? [
              {
                rowId: 1,
                height: 40,
                cells: [
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'header',
                    text: ''
                  },
                  ...Object.values(funds)
                    .map(item => [item.previous_fund, item])
                    .flat()
                    .map(fund => ({
                      type: 'header',
                      text: `${fund.period}, ${fund.year}`
                    }))
                ]
              }
            ]
          : []),
        ...Object.keys(columnLables).map((key, ind) => {
          let cells = isMultiFunds
            ? isComparisonReport
              ? Object.values(funds)
                  .map(value => [value, value.previous_fund])
                  .flat()
                  .map((value, j) =>
                    getColumnByType({
                      type: schema?.types?.[key]?.type,
                      options: schema?.types?.[key]?.options,
                      key,
                      defaultValue: schema?.defaultValues?.[key],
                      index,
                      value: value.fund_data[tableName][index],
                      required:
                        funds[value.fund_id]?.required_tables?.[tableName]?.field_names?.includes(
                          key
                        ),
                      tableName,
                      isStaticTemplate,
                      fundId: value.fund_id,
                      isSectionDisabled: isTableDisabledForFund(value.fund_id),
                      hasDrillDownFeature:
                        !!value.fund_data[tableName][index]?.report_id &&
                        schema.types[key].has_drill_down_feature &&
                        value.fund_data[tableName][index]?.[key] !== null,
                      reportId: value.fund_data[tableName][index]?.report_id,
                      tableId: value.fund_data[tableName]?.[index]?.id,
                      title: columnLables[key]?.name,
                      isMultiFunds,
                      isEsma: true,
                      warning: value.fund_data[tableName][index]?.warning?.[key],
                      fieldOrder: order + index,
                      fundName: value.fund_name,
                      difference:
                        j % 2 === 1
                          ? funds[value.fund_id]?.difference?.[tableName]?.[index]?.[key] || {}
                          : null,
                      nonEditable: isComparisonReport
                    })
                  )
              : Object.values(funds).map(value =>
                  getColumnByType({
                    type: schema?.types?.[key]?.type,
                    options: schema?.types?.[key]?.options,
                    key,
                    defaultValue: schema?.defaultValues?.[key],
                    index,
                    value: value.fund_data[tableName][index],
                    required:
                      funds[value.fund_id]?.required_tables?.[tableName]?.field_names?.includes(
                        key
                      ),
                    tableName,
                    isStaticTemplate,
                    fundId: value.fund_id,
                    isSectionDisabled: isTableDisabledForFund(value.fund_id),
                    hasDrillDownFeature:
                      !!value.fund_data[tableName][index]?.report_id &&
                      schema.types[key].has_drill_down_feature &&
                      value.fund_data[tableName][index]?.[key] !== null,
                    reportId: value.fund_data[tableName][index]?.report_id,
                    tableId: value.fund_data[tableName]?.[index]?.id,
                    title: columnLables[key]?.name,
                    isMultiFunds,
                    isEsma: true,
                    warning: value.fund_data[tableName][index]?.warning?.[key],
                    fieldOrder: order + index,
                    fundName: value.fund_name,
                    nonEditable: isComparisonReport
                  })
                )
            : [
                ...(isComparisonReport
                  ? [
                      getColumnByType({
                        type: schema?.types?.[key]?.type,
                        options: schema?.types?.[key]?.options,
                        key,
                        defaultValue: schema?.defaultValues?.[key],
                        index,
                        value: funds[currentDataKey].previous_fund.fund_data[tableName][index],
                        required: requiredFields.includes(key),
                        tableName,
                        isStaticTemplate,
                        isMultiFunds,
                        isEsma: true,
                        warning:
                          funds[currentDataKey].previous_fund.fund_data[tableName][index]
                            ?.warning?.[key],
                        fieldOrder: order + index,
                        fundId: funds[currentDataKey].previous_fund.fund_id,
                        isSectionDisabled: isTableDisabledForFund(
                          funds[currentDataKey].previous_fund
                        ),
                        fundName: funds[currentDataKey].previous_fund.fund_name,
                        nonEditable: isComparisonReport
                      })
                    ]
                  : []),
                getColumnByType({
                  type: schema?.types?.[key]?.type,
                  options: schema?.types?.[key]?.options,
                  key,
                  defaultValue: schema?.defaultValues?.[key],
                  index,
                  value: item,
                  required: requiredFields.includes(key),
                  tableName,
                  isStaticTemplate,
                  isMultiFunds,
                  isEsma: true,
                  warning: item?.warning?.[key],
                  fieldOrder: order + index,
                  fundId: item?.fund_id,
                  isSectionDisabled: isTableDisabledForFund(item?.fund_id),
                  fundName: item?.fund_name,
                  difference: isComparisonReport
                    ? funds[currentDataKey]?.difference?.[tableName]?.[index]?.[key] || {}
                    : null,
                  nonEditable: isComparisonReport
                })
              ];

          const headers = isMultiFunds
            ? Object.values(funds).map(() => ({
                type: 'header',
                text: columnLables[key].description
              }))
            : [
                {
                  type: 'header',
                  text: columnLables[key].description
                }
              ];

          // Each value column stacks its own value above its own error, so the
          // row must fit the tallest column — pairing the longest value with a
          // different column's longest error over-sizes it.
          const rows = isMultiFunds
            ? isComparisonReport
              ? Object.values(funds)
                  .map(fund => [fund.previous_fund, fund])
                  .flat()
                  .map(fund => fund.fund_data[tableName]?.[index])
              : Object.values(funds).map(fund => fund.fund_data[tableName]?.[index])
            : isComparisonReport
              ? [funds[currentDataKey].previous_fund.fund_data[tableName][index], item]
              : [item];

          const rowHeight = getRowHeight({
            label: columnLables[key]?.name,
            labelWidth,
            cells: getRowCells(rows, key, schema?.types?.[key]?.options),
            valueWidth
          });

          return {
            rowId: isMultiFunds && isComparisonReport ? ind + 2 : ind + 1,
            height: rowHeight,
            cells: columnLables[key].isStatic
              ? [
                  {
                    type: 'header',
                    text: ''
                  },
                  {
                    type: 'header',
                    text: columnLables[key].name
                  },
                  ...headers
                ]
              : [
                  {
                    type: 'header',
                    text: `${ind + order + (hasOrderNumber ? 1 : 0)}`
                  },
                  {
                    // type: 'header',
                    // text: columnLables[key].name,

                    type:
                      data[index]?.report_id &&
                      data[index]?.[key] !== null &&
                      schema.types[key]?.has_drill_down_feature
                        ? 'customHeader'
                        : 'header',
                    text: columnLables[key]?.name || '',
                    render: () => (
                      <CustomHeaderWithDrillDown
                        title={columnLables[key]?.name}
                        reportId={data[index]?.report_id}
                        fundId={data[index].fund_id}
                        columnName={key}
                        tableName={tableName}
                        tableId={data[index]?.id}
                      />
                    ),
                    style: {
                      'justify-content': 'flex-start',
                      'white-space': 'inherit'
                    }
                  },
                  ...cells
                ]
          };
        })
      ];
    });
  }, [data, isMultiFunds, funds, isStaticTemplate, isComparisonReport]);

  return { columns, onChange, tables };
}
